// ============================================
// VOICE BOT API CLIENT
// ============================================
// Thin URL/header wrappers around the iLand widget backend.
// Extracted from VoiceBotPanel.tsx in Wave 2 of the widget refactor.
//
// Design rules (zero behavior change):
// - Every function returns the raw `Response` (or sendBeacon return value).
//   Callers keep their own `.ok` checks, `.json()` parsing, retry logic,
//   and state mutations untouched.
// - Each function takes `publicKey` as an explicit optional argument.
//   Callers decide whether to read `config.publicKey` (state) or
//   `configRef.current.publicKey` (ref) — the wrapper does not know.
// - Header construction is identical to the original inline code:
//     { 'Content-Type': 'application/json', ...(publicKey ? { 'X-Iland-Public-Key': publicKey } : {}) }
//   so spread-order and object identity match what Vite previously emitted.
// - Endpoints that the original code did NOT send the public key with
//   (transcript, calendly-event, calendly-slots, calendly-check-slot,
//    calendly-book) keep the same plain headers — no publicKey param.
//
// Out of scope (intentionally NOT wrapped — these are not iLand API):
// - Azure OpenAI Realtime SDP exchange (POST <webrtcUrl>)
// - Direct Supabase Storage PUT to presigned URLs

import { getApiUrl } from '../config/api'

const jsonHeadersWithKey = (publicKey?: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(publicKey ? { 'X-Iland-Public-Key': publicKey } : {}),
})

const PLAIN_JSON_HEADERS = { 'Content-Type': 'application/json' } as const

// ----- Calendly --------------------------------------------------------------

export const fetchCalendlyEvent = (eventUri: string, customerId?: string): Promise<Response> =>
  fetch(getApiUrl(`/api/calendly-event?uri=${encodeURIComponent(eventUri)}${customerId ? `&customerId=${customerId}` : ''}`))

export const fetchCalendlySlots = (
  timePreference: string | undefined,
  customerId: string | undefined,
  preferredDate?: string,
): Promise<Response> =>
  fetch(getApiUrl(`/api/calendly-slots?timePreference=${timePreference || 'any'}${customerId ? `&customerId=${customerId}` : ''}${preferredDate ? `&startDate=${preferredDate}` : ''}`))

export const postCalendlyCheckSlot = (body: unknown): Promise<Response> =>
  fetch(getApiUrl('/api/calendly-check-slot'), {
    method: 'POST',
    headers: PLAIN_JSON_HEADERS,
    body: JSON.stringify(body),
  })

export const postCalendlyBook = (body: unknown): Promise<Response> =>
  fetch(getApiUrl('/api/calendly-book'), {
    method: 'POST',
    headers: PLAIN_JSON_HEADERS,
    body: JSON.stringify(body),
  })

// ----- Knowledge base --------------------------------------------------------

export const postKnowledgeBase = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/knowledge-base'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

// ----- Residents (Dona) ת.ז format pre-check -------------------------------
// Voice-only: validates the FORMAT + checksum of a spoken Israeli ID (ת.ז)
// via the deterministic server endpoint (no DB lookup → no membership oracle).
// Mirrors the chat-side verify_resident_id pre-check. Returns the raw Response;
// the caller parses { valid: boolean }.
export const postResidentVerifyId = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/residents-verify-id'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

// ----- Voice session ---------------------------------------------------------

export const postVoiceSession = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/voice-session'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

// ----- Chat session ---------------------------------------------------------
// PR 6/7 of Multi-Channel Assistant rollout. Text-only Realtime session
// — same auth + customer gating as voice, but server omits voice/audio
// config and returns channel='chat'.
export const postChatSession = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/chat-session'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

// PR 9 (Phase 5) — HTTP chat-message endpoint. Replaces the WebRTC
// data-channel transport for chat (voice still uses Realtime WebRTC).
// Stateless per turn: caller passes prior `messages` history + new
// `userMessage` (or empty body to fetch the opening greeting).
// Accepts an optional AbortSignal so the widget can cancel in-flight
// requests on unmount / reconnect (Phase 5.1).
export const postChatMessage = (
  body: unknown,
  publicKey?: string,
  signal?: AbortSignal,
): Promise<Response> =>
  fetch(getApiUrl('/api/chat-message'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
    signal,
  })

// Phase 5.2 — SSE streaming variant of /api/chat-message. Same body as
// postChatMessage plus `stream: true`, and an Accept: text/event-stream
// header so the server returns a token stream instead of a buffered JSON
// reply. The caller reads `response.body` (a ReadableStream) and parses
// the SSE events (meta / tool / token / done / error). Chat-only — voice
// still uses Azure Realtime WebRTC and is untouched.
export const postChatMessageStream = (
  body: unknown,
  publicKey?: string,
  signal?: AbortSignal,
): Promise<Response> =>
  fetch(getApiUrl('/api/chat-message'), {
    method: 'POST',
    headers: { ...jsonHeadersWithKey(publicKey), Accept: 'text/event-stream' },
    body: JSON.stringify({ ...(body as Record<string, unknown>), stream: true }),
    signal,
  })

// PR 8 — chat transcript / close. Mirrors postTranscript / sendTranscriptBeacon
// but routes to /api/chat-transcript which dedupes by `chat_session:<id>`
// idempotency key and gates billing behind CHAT_BILLING_ENABLED.
export const postChatTranscript = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/chat-transcript'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

export const sendChatTranscriptBeacon = (body: unknown, publicKey?: string): boolean => {
  // sendBeacon doesn't accept custom headers, so we inline the public
  // key into the JSON body — chat-transcript.js already accepts it via
  // `req.body?.publicKey` as a fallback.
  const payload = publicKey
    ? { ...(body as Record<string, unknown>), publicKey }
    : (body as Record<string, unknown>)
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
  return navigator.sendBeacon(getApiUrl('/api/chat-transcript'), blob)
}

// ----- Recording upload ------------------------------------------------------

export const postUploadChunk = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/upload-chunk'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

export const postConfirmChunkUpload = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/confirm-chunk-upload'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

export const postFinalizeRecording = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/finalize-recording'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

export const postGetUploadUrl = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/get-upload-url'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

export const postConfirmUpload = (body: unknown, publicKey?: string): Promise<Response> =>
  fetch(getApiUrl('/api/confirm-upload'), {
    method: 'POST',
    headers: jsonHeadersWithKey(publicKey),
    body: JSON.stringify(body),
  })

// ----- Transcript ------------------------------------------------------------

export const postTranscript = (body: unknown): Promise<Response> =>
  fetch(getApiUrl('/api/transcript'), {
    method: 'POST',
    headers: PLAIN_JSON_HEADERS,
    body: JSON.stringify(body),
  })

export const sendTranscriptBeacon = (body: unknown): boolean => {
  const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
  return navigator.sendBeacon(getApiUrl('/api/transcript'), blob)
}
